import AuthCtx from '@auth/AuthCtx';
import { Loading } from '@components/loadling';
import { GenCtx } from '@hooks/generate';
import { initSdk, onApiError } from '@proccess/api';
import { CurrentEnv } from '@proccess/constants';
import sdk from '@proccess/sdk';
import { AxiosResponse } from 'axios';
import { forEach, isEmpty, unset } from 'lodash';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

type IForm = {
  fields: IHospital;
};

export const HospitalCtx = GenCtx({
  useLogic() {
    const { user } = AuthCtx.useCtx();
    const router = useRouter();
    const { enqueueSnackbar } = useSnackbar();

    sdk.options.AppId = String(CurrentEnv?.APP_ID);

    const [hospitals, setHospitals] = useState([] as IHospital[]);
    const [hospital, setHospital] = useState({} as IHospital);
    const [logoImageFile, setLogoImageFile] = useState<File | null>(null);
    const methods = useForm<IForm>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        fields: {
          featured_news: [],
        },
      },
    });

    const { setValue } = methods;

    const fieldArray = useFieldArray({
      control: methods.control,
      name: 'fields.featured_news',
    });

    const onSaveHospital = async ({ fields }: IForm) => {
      try {
        Loading.Show();

        const formData: any = new FormData();

        if (logoImageFile) {
          formData.append('logo_image', logoImageFile, logoImageFile?.name);
        }
        console.log('fields', fields);
        const fieldsCopy = { ...fields, featured_news: JSON.stringify(fields.featured_news || []) };

        unset(fieldsCopy, 'features');

        forEach(Object.entries(fieldsCopy), ([key, value]) => {
          formData.append(key, value as any);
        });

        const { data }: AxiosResponse<IAppResult<'hospital', IHospital>> = await initSdk.patch(
          `/admin/hospitals/${hospital?.id}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              HospitalId: hospital?.id,
            },
          }
        );
        setHospital(data?.hospital);
        enqueueSnackbar('Cập nhật thông tin bệnh viện thành công !');
      } catch (error) {
        onApiError(error);
      } finally {
        Loading.Hide();
      }
    };

    const handleDrop = useCallback((acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      setLogoImageFile(newFile);
      if (file) {
        setValue('fields.logo_image_url', newFile.preview, { shouldValidate: true });
      }
    }, []);

    const handleRemoveFile = () => {
      setValue('fields.logo_image_url', null);
    };

    async function onGetHospitalDetails({ id }: { id?: number }) {
      try {
        Loading.Show();
        const { data }: AxiosResponse<IAppResult<'hospital', IHospital>> = await initSdk.get(
          `/admin/hospitals/${id}`,
          {
            headers: {
              HospitalId: id,
            },
          }
        );
        setHospital(data?.hospital);
        localStorage.setItem('hospitalid', String(id));
        sdk.options.HospitalId = Number(id);
        setValue('fields', data?.hospital);
        fieldArray.replace(data?.hospital.featured_news || []);
      } catch (error) {
        onApiError(error);
      } finally {
        Loading.Hide();
      }
    }

    async function onAddConfigNews() {
      fieldArray.append({
        title: '',
        article_category_slug: '',
        article_type: '',
      });
    }
    async function onRemoveConfigNews(index: number) {
      fieldArray.remove(index);
    }

    async function onGetHospitals() {
      try {
        Loading.Show();
        const { data }: AxiosResponse<IHospital[]> = await initSdk.get(
          `/hospitals/hospitals_by_app_id`
        );
        setHospitals(data);
        return data;
      } catch (error) {
        onApiError(error);
        return [];
      } finally {
        Loading.Hide();
      }
    }

    useEffect(() => {
      if (!router.isReady) return;
      if (isEmpty(user)) return;

      const onInit = async () => {
        const hList = await onGetHospitals();
        let hospitalId = localStorage.getItem('hospitalid');
        if (!hospitalId && hList && hList.length > 0) {
          hospitalId = String(hList[0].id);
        }
        if (hospitalId) {
          onGetHospitalDetails({ id: Number(hospitalId) });
        }
      };

      onInit();
      window.addEventListener('loginSuccess', onInit);
      return () => {
        window.removeEventListener('loginSuccess', onInit);
      };
    }, [router.isReady, user]);

    return {
      methods,
      fieldArray,
      hospitals,
      hospital,
      fncs: {
        onGetHospitalDetails,
        onSaveHospital,
        handleDrop,
        handleRemoveFile,
        onAddConfigNews,
        onRemoveConfigNews,
      },
    };
  },
});
